import { ColumnHelper, HeaderContext } from "@tanstack/react-table";
import { GenerateColumnsFn } from "PFComponents/table/table.types";
import { Typography } from "PFComponents/typography/typography";
import { ProfileNameLink } from "PFCore/helpers/profile_name_link";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { CustomValue } from "PFTypes";
import { useCallback } from "react";
import React from "react";
import { useTranslation } from "react-i18next";

import { SkillActions } from "../cell/skill_actions";
import { SkillState } from "../cell/skill_state";
import { SkillTitleButton } from "../cell/skill_title_button";
import { useSkillsManagementContext } from "../contexts/skills_management_context_provider";
import css from "../skills_management_table.module.scss";
import { useSkillManagementCounters } from "./use_skill_management_counters";
import { useSkillManagementSidePanel } from "./use_skill_management_side_panel";

type TableData = {
  skills?: CustomValue[];
  generateColumns: GenerateColumnsFn<CustomValue>;
  totalPages: number;
  isLoading: boolean;
  skillsCounter?: number;
  approvalsCounter?: number;
  blockedCounter?: number;
  mergedCounter?: number;
};

export const useTableData = (): TableData => {
  const { t } = useTranslation("skillsManagement");
  const { skillsData, selectedCounter } = useSkillsManagementContext();
  const { data, isLoading } = skillsData;
  const { closeSidePanel } = useSkillManagementSidePanel();

  const { formatISODate } = useDateFormatter();

  const { total, ...counters } = useSkillManagementCounters();
  const skills = data?.entries;
  const totalPages = data?.meta?.totalPages || 0;

  const header = useCallback(
    (header: HeaderContext<CustomValue, unknown>) => (
      <Typography
        variant="h5"
        className={header.column.id === "actions" ? css.actionsColumnHeader : undefined}
      >
        {t(`table.headers.${header.header.id}` as unknown as TemplateStringsArray)}
      </Typography>
    ),
    [t]
  );

  const generateColumns = useCallback(
    (columnHelper: ColumnHelper<CustomValue>) => [
      columnHelper.accessor((row) => row.value, {
        id: "value",
        header,
        enableSorting: true,
        cell: (info) => info.getValue() && <SkillTitleButton skill={info.row.original} />
      }),
      columnHelper.accessor((row) => row.state, {
        id: "state",
        header,
        enableSorting: false,
        size: 200,
        cell: (info) => info.getValue() && <SkillState state={info.getValue()} />
      }),
      columnHelper.accessor((row) => row.creator?.name, {
        id: "creator",
        header,
        enableSorting: false,
        size: 200,
        cell: (info) =>
          info.row.original.creator?.id &&
          info.row.original.creator?.name && (
            <Typography variant={"labelRegular"}>
              <ProfileNameLink
                profile={{
                  id: info.row.original.creator?.id,
                  firstName: info.row.original.creator?.name.split(" ")[0],
                  lastName: info.row.original.creator?.name.split(" ")[1],
                  viewable: true
                }}
                options={{ beforeShowPreview: closeSidePanel }}
              />
            </Typography>
          )
      }),
      columnHelper.accessor((row) => row.created_at, {
        id: "created_at",
        header,
        enableSorting: true,
        size: 200,
        cell: (info) =>
          info.getValue() && (
            <Typography variant={"labelRegular"}>{formatISODate(info.getValue())}</Typography>
          )
      }),
      columnHelper.accessor((row) => row, {
        id: "actions",
        header,
        enableSorting: false,
        cell: (info) =>
          info.getValue() && <SkillActions skill={info.getValue()} selectedCounter={selectedCounter} />,
        size: 100
      })
    ],
    [formatISODate, header, selectedCounter, closeSidePanel]
  );

  return {
    skills,
    generateColumns,
    totalPages,
    isLoading,
    skillsCounter: total,
    approvalsCounter: counters.approved,
    blockedCounter: counters.blocked
  };
};
